import React, { useMemo, useState } from 'react';
import { Alert, Col, Collapse, Form, Row } from 'react-bootstrap';
import Select from 'react-select';

import {
  MUSCULOSKELETAL_FORM_ID_PREFIXES,
} from '../../tools/dbq/dbqMusculoskeletalConstants';
import {
  DBQ_CONTENTION_OPTION_GROUPS,
  findDbqContentionOptions,
} from './dbqConditionOptions';
import DbqEvidencePackagePicker from './DbqEvidencePackagePicker';
import {
  filterAiAutomatedFieldConfigsByAccess,
  normalizeAiAutomatedFields,
} from './dbqWorkspaceUtils';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderColor: state.isFocused ? '#86b7fe' : base.borderColor,
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(13, 110, 253, 0.25)' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? '#86b7fe' : base.borderColor,
    },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 30,
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#e7f1ff',
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#084298',
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#084298',
    ':hover': {
      backgroundColor: '#cfe2ff',
      color: '#052c65',
    },
  }),
};

const formatDbqOptionLabel = (option) => option.label;

const formatContentionOptionLabel = (option, { context }) => {
  if (context === 'value') {
    return option.leafLabel || option.label;
  }

  return (
    <div>
      <div>{option.leafLabel || option.label}</div>
      {option.pathLabel ? (
        <div className="text-muted small">{option.rootLabel} / {option.pathLabel}</div>
      ) : (
        <div className="text-muted small">{option.rootLabel}</div>
      )}
    </div>
  );
};

const normalizeFormIdentifier = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const isMusculoskeletalDbqOption = (draft = {}, selectedDbqOption = null) => {
  const identifiers = [
    selectedDbqOption?.formId,
    selectedDbqOption?.shortName,
    selectedDbqOption?.title,
    selectedDbqOption?.label,
    draft.dbqStableFormId,
    draft.dbqShortName,
    draft.dbqTitle,
    draft.dbqFormId,
  ].map(normalizeFormIdentifier).filter(Boolean);

  return identifiers.some((identifier) =>
    MUSCULOSKELETAL_FORM_ID_PREFIXES.some((prefix) =>
      identifier.startsWith(normalizeFormIdentifier(prefix))),
  );
};

const DbqRequestWorkspaceForm = ({
  draft,
  mode = 'create',
  dbqFormOptions = [],
  toeOptions = [],
  providerOptions = [],
  claimEvidenceFiles = [],
  isSavingRequest = false,
  isLoadingToeOptions = false,
  dd214EvidenceWarning = null,
  expandedAiAutomatedFieldsEnabled = false,
  onFieldChange,
  onRequestContentionToeChange,
  onEvidenceToggle,
}) => {
  const [expandedPromptFields, setExpandedPromptFields] = useState({});
  const selectedDbqOption = useMemo(
    () => dbqFormOptions.find((option) => option.value === draft.dbqFormId) || null,
    [dbqFormOptions, draft.dbqFormId],
  );
  const isSelectedDbqMusculoskeletal = useMemo(
    () => isMusculoskeletalDbqOption(draft, selectedDbqOption),
    [draft, selectedDbqOption],
  );

  const selectedContentionOptions = useMemo(
    () => findDbqContentionOptions(draft.selectedContentions),
    [draft.selectedContentions],
  );
  const requestContentions = useMemo(
    () => (Array.isArray(draft.requestContentions) ? draft.requestContentions : []),
    [draft.requestContentions],
  );
  const aiAutomatedFields = useMemo(
    () =>
      normalizeAiAutomatedFields(draft.aiAutomatedFields, {
        evidenceReviewPrompt: draft.evidenceReviewPrompt,
        autoGenerateMedicalOpinion: draft.autoGenerateMedicalOpinion,
        moPrompt: draft.moPrompt,
      }),
    [
      draft.aiAutomatedFields,
      draft.autoGenerateMedicalOpinion,
      draft.evidenceReviewPrompt,
      draft.moPrompt,
    ],
  );
  const visibleAiAutomatedFieldConfigs = useMemo(
    () =>
      filterAiAutomatedFieldConfigsByAccess(undefined, {
        expandedAiAutomatedFieldsEnabled,
        isSelectedDbqMusculoskeletal,
      }),
    [expandedAiAutomatedFieldsEnabled, isSelectedDbqMusculoskeletal],
  );
  const enabledAiFieldCount = useMemo(
    () =>
      visibleAiAutomatedFieldConfigs.filter(
        (config) => aiAutomatedFields?.[config.key]?.enabled !== false,
      ).length,
    [aiAutomatedFields, visibleAiAutomatedFieldConfigs],
  );

  const handleAiAutomatedFieldChange = (fieldKey, patch) => {
    onFieldChange('aiAutomatedFields', {
      ...aiAutomatedFields,
      [fieldKey]: {
        ...aiAutomatedFields[fieldKey],
        ...patch,
      },
    });
  };

  const togglePromptField = (fieldKey) => {
    setExpandedPromptFields((current) => ({
      ...current,
      [fieldKey]: !current[fieldKey],
    }));
  };

  return (
    <Row className="g-3">
      <Col md={12}>
        <Form.Group>
          <Form.Label>
            DBQ <span className="text-danger">*</span>
          </Form.Label>
          {mode === 'create' ? (
            <Select
              inputId="dbq-request-form-select"
              options={dbqFormOptions}
              value={selectedDbqOption}
              onChange={(option) => {
                onFieldChange('dbqFormId', option?.value || '');
                onFieldChange('dbqStableFormId', option?.formId || '');
                onFieldChange('dbqShortName', option?.shortName || '');
                onFieldChange('dbqTitle', option?.label || '');
              }}
              placeholder="Select a DBQ..."
              isClearable
              isSearchable
              isDisabled={isSavingRequest}
              styles={selectStyles}
              formatOptionLabel={formatDbqOptionLabel}
              noOptionsMessage={() => 'No DBQs found'}
            />
          ) : (
            <Form.Control type="text" value={draft.dbqTitle || ''} readOnly />
          )}
        </Form.Group>
      </Col>

      <Col md={12}>
        <Form.Group>
          <Form.Label>
            Contentions <span className="text-danger">*</span>
          </Form.Label>
          <Select
            inputId="dbq-request-contentions-select"
            isMulti
            options={DBQ_CONTENTION_OPTION_GROUPS}
            value={selectedContentionOptions}
            onChange={(options) =>
              onFieldChange(
                'selectedContentions',
                Array.isArray(options) ? options.map((option) => option.value) : [],
              )
            }
            placeholder="Search and select contentions..."
            isSearchable
            isDisabled={isSavingRequest}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            styles={selectStyles}
            formatOptionLabel={formatContentionOptionLabel}
            noOptionsMessage={() => 'No selectable contentions found'}
          />
          <Form.Text className="text-muted">
            Only the starred contention entries from the hierarchy are selectable. You can pick more
            than one.
          </Form.Text>
        </Form.Group>
      </Col>

      <Col md={12}>
        <Form.Group>
          <Form.Label>
            Theory of Entitlement (TOE) Per Contention <span className="text-danger">*</span>
          </Form.Label>
          {requestContentions.length === 0 ? (
            <Alert variant="light" className="mb-0">
              Select at least one contention to assign its TOE.
            </Alert>
          ) : (
            <div className="d-flex flex-column gap-2">
              {requestContentions.map((requestContention) => (
                <div
                  key={requestContention.contention}
                  className="border rounded p-3"
                  style={{ background: '#f8f9fb' }}
                >
                  <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                    {requestContention.contentionLabel || requestContention.contention}
                  </div>
                  <div className="text-muted small mb-2">
                    {requestContention.contention}
                  </div>
                  <Form.Select
                    value={requestContention.toeId || ''}
                    onChange={(event) =>
                      onRequestContentionToeChange?.(
                        requestContention.contention,
                        event.target.value,
                      )
                    }
                    disabled={isSavingRequest || isLoadingToeOptions}
                  >
                    <option value="">-- Select TOE --</option>
                    {toeOptions.map((toe) => (
                      <option key={toe.toeId} value={toe.toeId}>
                        {toe.title || toe.toeType || toe.toeId}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              ))}
            </div>
          )}
          <Form.Text className="text-muted">
            Every selected contention must have its own TOE before the request can be saved.
          </Form.Text>
        </Form.Group>
      </Col>

      <Col md={6}>
        <Form.Group>
          <Form.Label>
            Assigned Provider <span className="text-danger">*</span>
          </Form.Label>
          {providerOptions.length === 0 ? (
            <Alert variant="warning" className="mb-0">
              No providers are assigned to this claim yet.
            </Alert>
          ) : (
            <Form.Select
              value={draft.providerUserId}
              onChange={(event) => onFieldChange('providerUserId', event.target.value)}
              disabled={isSavingRequest}
            >
              <option value="">-- Select provider --</option>
              {providerOptions.map((provider) => (
                <option key={provider.userId} value={provider.userId}>
                  {provider.name} ({provider.email})
                </option>
              ))}
            </Form.Select>
          )}
          <Form.Text className="text-muted">
            Provider assignment is saved on the request and reused by the send flow.
          </Form.Text>
        </Form.Group>
      </Col>

      <Col md={12}>
        <Form.Group>
          <Form.Label>Evidence Package</Form.Label>
          {dd214EvidenceWarning ? (
            <Alert variant="warning" className="mb-3">
              {dd214EvidenceWarning}
            </Alert>
          ) : null}
          <DbqEvidencePackagePicker
            claimEvidenceFiles={claimEvidenceFiles}
            selectedEvidenceFileIds={draft.selectedEvidenceFileIds}
            isSavingRequest={isSavingRequest}
            onEvidenceToggle={onEvidenceToggle}
          />
          <Form.Text className="text-muted">
            Select the claim files that should travel with this request package.
          </Form.Text>
        </Form.Group>
      </Col>

      <Col md={12}>
        <Form.Group>
          <Form.Label>Notes</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            placeholder="Add any request context, instructions, or callouts for the provider."
            value={draft.requestNotes}
            onChange={(event) => onFieldChange('requestNotes', event.target.value)}
            disabled={isSavingRequest}
          />
        </Form.Group>
      </Col>

      <Col md={12}>
        <div
          className="rounded-4 p-3 p-md-4"
          style={{
            border: '1px solid #d6e0eb',
            background: 'linear-gradient(180deg, #fbfdff 0%, #f6f9fc 100%)',
          }}
        >
          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
            <div>
              <div
                className="text-uppercase fw-semibold text-muted"
                style={{ letterSpacing: '0.08em', fontSize: '0.72rem' }}
              >
                Automation
              </div>
              <div className="fw-semibold" style={{ fontSize: '1rem' }}>
                AI Automated Fields
              </div>
              <div className="text-muted small mt-1" style={{ maxWidth: 660 }}>
                Choose which drafts should be generated with this request. Prompts are optional
                and saved per field.
              </div>
            </div>
            <div
              className="rounded-pill px-3 py-2 fw-semibold"
              style={{
                background: '#e8f1fb',
                color: '#23435f',
                fontSize: '0.82rem',
                whiteSpace: 'nowrap',
              }}
            >
              {enabledAiFieldCount} of {visibleAiAutomatedFieldConfigs.length} enabled
            </div>
          </div>

          <div className="mt-4 d-flex flex-column gap-2">
            {visibleAiAutomatedFieldConfigs.map((config, index) => {
              const fieldConfig = aiAutomatedFields[config.key] || {
                enabled: true,
                prompt: '',
              };
              const promptText = fieldConfig.prompt || '';
              const isPromptExpanded =
                typeof expandedPromptFields[config.key] === 'boolean'
                  ? expandedPromptFields[config.key]
                  : Boolean(promptText);

              return (
                <div
                  key={config.key}
                  className="rounded-3 overflow-hidden"
                  style={{
                    border: fieldConfig.enabled
                      ? '1px solid rgba(69, 113, 154, 0.24)'
                      : '1px solid rgba(108, 117, 125, 0.18)',
                    background: fieldConfig.enabled ? '#ffffff' : '#f8f9fa',
                    transition: 'border-color 140ms ease, background-color 140ms ease',
                  }}
                >
                  <div className="p-3">
                    <div className="d-flex justify-content-between align-items-start gap-3">
                      <div className="d-flex gap-3">
                        <div
                          className="text-muted fw-semibold"
                          style={{
                            fontVariantNumeric: 'tabular-nums',
                            minWidth: 24,
                            paddingTop: 1,
                          }}
                        >
                          {String(index + 1).padStart(2, '0')}
                        </div>
                        <div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className="fw-semibold">{config.label}</span>
                            {config.requiresMusculoskeletal ? (
                              <span
                                className="rounded-pill px-2 py-1 text-muted"
                                style={{ background: '#f1f4f7', fontSize: '0.7rem' }}
                              >
                                MSK
                              </span>
                            ) : null}
                            {promptText && !isPromptExpanded ? (
                              <span
                                className="rounded-pill px-2 py-1"
                                style={{
                                  background: '#eaf4ee',
                                  color: '#46614f',
                                  fontSize: '0.7rem',
                                }}
                              >
                                Prompt set
                              </span>
                            ) : null}
                          </div>
                          <div className="text-muted small mt-1">{config.description}</div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-3 flex-shrink-0">
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-decoration-none"
                          onClick={() => togglePromptField(config.key)}
                          aria-expanded={isPromptExpanded}
                          aria-controls={`dbq-request-ai-prompt-${config.key}`}
                        >
                          {isPromptExpanded ? 'Hide prompt' : 'Prompt'}
                        </button>
                        <Form.Check
                          type="switch"
                          id={`dbq-request-ai-field-${config.key}`}
                          label="Generate"
                          checked={fieldConfig.enabled !== false}
                          onChange={(event) =>
                            handleAiAutomatedFieldChange(config.key, {
                              enabled: event.target.checked,
                            })
                          }
                          disabled={isSavingRequest}
                        />
                      </div>
                    </div>
                    <Collapse in={isPromptExpanded}>
                      <div id={`dbq-request-ai-prompt-${config.key}`}>
                        <Form.Group className="mt-3 mb-0">
                          <Form.Label className="mb-1 small text-muted">
                            Prompt guidance
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder={config.promptPlaceholder}
                            value={promptText}
                            onChange={(event) =>
                              handleAiAutomatedFieldChange(config.key, {
                                prompt: event.target.value,
                              })
                            }
                            disabled={isSavingRequest}
                          />
                        </Form.Group>
                      </div>
                    </Collapse>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default DbqRequestWorkspaceForm;
